import { isRole } from '@studio/access';
import { type CurrentUserModern, UserType } from '@studio/api-users/types';
import { textDecoder } from '@studio/utils/codecs';
import type { PublishingSite } from '@studio/utils/experience';

import type { StudioIdentityToken, StudioIdentityTokenPayload } from './types';

export function getTokenPayload(
  token: StudioIdentityToken,
): StudioIdentityTokenPayload {
  const [_header, payload, _signature] = token.split('.');

  const payloadBytes = Uint8Array.fromBase64(payload!, {
    alphabet: 'base64url',
  });
  const payloadString = textDecoder.decode(payloadBytes);
  const payloadJson = JSON.parse(payloadString);

  return payloadJson;
}

const PUBLISHING_SITE_ROLE_PREFIX = 'publishingsite:';

export enum SNOWTicketState {
  Open = 'Open',
  Closed = 'Closed',
  ClosedComplete = 'Closed Complete',
  InProgress = 'In Progress',
  Cancelled = 'Canceled',
  Unknown = 'Unknown',
}

export enum SNOWTicketStage {
  WaitingForApproval = 'Waiting for Approval',
  Approved = 'Approved',
  Completed = 'Completed',
  Rejected = 'Rejected',
  Requested = 'Requested',
  Unknown = 'Unknown',
}

export type PublishingSiteRole<Name extends PublishingSite = PublishingSite> =
  `${typeof PUBLISHING_SITE_ROLE_PREFIX}${Name}`;

export function isPublishingSiteRole(role: string): role is PublishingSiteRole {
  return role.startsWith(PUBLISHING_SITE_ROLE_PREFIX);
}

export function extractPublishingSite<Role extends PublishingSiteRole>(
  publishingSiteRole: Role,
): Role extends PublishingSiteRole<infer Name> ? Name : never {
  return publishingSiteRole.slice(PUBLISHING_SITE_ROLE_PREFIX.length) as any;
}

export function getPublishingSiteRole(
  publishingSite: string,
): PublishingSiteRole {
  return (PUBLISHING_SITE_ROLE_PREFIX + publishingSite) as PublishingSiteRole;
}

export function createCurrentUserFromTokenPayload(
  tokenPayload: StudioIdentityTokenPayload,
): CurrentUserModern {
  return {
    type: UserType.Modern,
    id: tokenPayload.sub,
    email: tokenPayload.email,
    username: tokenPayload.email,
    realm: tokenPayload.realm,
    roles: tokenPayload.roles.filter(isRole).sort(),
    legacyRoles: tokenPayload.legacyRoles ?? tokenPayload.roles,
    legacyRoleConditions: tokenPayload.legacyRoleConditions ?? {},
    groups: tokenPayload.groups.toSorted(),
    allowedPublishingSites: (tokenPayload.legacyRoles ?? tokenPayload.roles)
      .filter(isPublishingSiteRole)
      .map(extractPublishingSite),
    selectedPublishingSite: undefined,
    masqueraded: tokenPayload.masqueraded ?? false,
  };
}
